import * as THREE from 'three';
import { SwordTracker, type SwordFrame } from '../sword/tracking';
import {
  sweepBladeAgainst,
  bladeInsideCollider,
  cylinderCollider,
  type BladePose,
  type SweepHit,
} from './sweep';
import { edgeAlignmentAngle, classifyEdge, isThrust } from './edgeAlignment';
import { projectToFrontalPlane } from './frontalProjection';
import { classifyStroke, type StrokeClassification } from './strokeClassify';
import { zoneFromImpactY } from '../pell/zones';
import {
  CUT_PROJECTION_MIN_RATIO,
  EDGE_QUALITY,
  PELL_DEFAULT_RADIUS_M,
  PELL_STRIKE_ARC_MOTION_MPS,
  PELL_STRIKE_MIN_ARC_DEFAULT_M,
  PELL_STRIKE_MIN_SPEED_DEFAULT_MPS,
  SWORD_BLADE_LENGTH_M,
  SWORD_GRIP_LENGTH_M,
  type DominantHand,
  type EdgeQuality,
  type Zone,
} from '../config/constants';

/**
 * A confirmed impact on the pell, fully classified. Everything a caller needs to
 * score the rep, log the shot, and fire feedback — without re-touching the frame.
 */
export type Strike = {
  /** World contact point on the pell surface. */
  point: THREE.Vector3;
  quality: EdgeQuality;
  zone: Zone;
  /** Raw single-frame tip speed (m/s) — drives feedback intensity. */
  speed: number;
  /** Edge-alignment angle (rad); 0 for a colpo di punta. */
  edgeAngleRad: number;
  edgeDir: THREE.Vector3;
  /** Velocity component ⟂ to the blade axis (the cut trajectory). */
  cutDir: THREE.Vector3;
  /** Cut angle in the viewer-facing plane (deg), or null when ill-defined. */
  cutAngleDeg: number | null;
  /** Tip-path length (m) of the swing that led to this impact — scales the cut decal. */
  swingArcM: number;
  /** Named colpo from the impact velocity; null for a thrust or when undecidable. */
  stroke: StrokeClassification | null;
};

export type StrikeDetectorOptions = {
  pell: THREE.Mesh;
  camera: THREE.Camera;
  dominantHand: DominantHand;
  userHeightCm: number;
  /** Minimum impact tip speed (m/s) for a contact to count as a strike. */
  minSpeedMPerSec?: number;
  /** Minimum length (m) of the swing arc that led to impact. */
  minArcM?: number;
};

/**
 * Owns the full pell hit-detection pipeline behind one interface: blade sweep, the
 * outside→inside latch, edge/quality/zone classification, frontal projection, and
 * stroke naming. The session runtime calls {@link detect} once per frame and gets a
 * fully-formed {@link Strike} or null — it never sees the frame, the latch, the
 * collider, or the NaN/null gates.
 */
export class StrikeDetector {
  private tracker = new SwordTracker(SWORD_BLADE_LENGTH_M, SWORD_GRIP_LENGTH_M);
  private prevBlade: BladePose | null = null;
  /** Latch: a strike registers only on the outside→inside transition. */
  private bladeOutsidePell = true;
  /** Tip-path length (m) accumulated over the current swing; reset when the blade rests. */
  private swingArcM = 0;
  private readonly pellBase = new THREE.Vector3();
  private pell: THREE.Mesh;
  private camera: THREE.Camera;
  private dominantHand: DominantHand;
  private userHeightCm: number;
  private pellHeightM: number;
  private pellRadiusM: number;
  private minSpeedMPerSec: number;
  private minArcM: number;

  constructor(opts: StrikeDetectorOptions) {
    this.pell = opts.pell;
    this.camera = opts.camera;
    this.dominantHand = opts.dominantHand;
    this.userHeightCm = opts.userHeightCm;
    this.minSpeedMPerSec = opts.minSpeedMPerSec ?? PELL_STRIKE_MIN_SPEED_DEFAULT_MPS;
    this.minArcM = opts.minArcM ?? PELL_STRIKE_MIN_ARC_DEFAULT_M;
    this.pellHeightM = this.readPellHeightM(opts.pell);
    this.pellRadiusM = this.readPellRadiusM(opts.pell);
  }

  /** Clear cross-frame state (session start, controller swap). */
  reset(): void {
    this.tracker.reset();
    this.prevBlade = null;
    this.bladeOutsidePell = true;
    this.swingArcM = 0;
  }

  /** Minimum impact tip speed (m/s) a contact must reach to count as a strike. */
  setMinSpeedMPerSec(v: number): void {
    this.minSpeedMPerSec = v;
  }

  /** Minimum swing-arc length (m) a contact must reach to count as a strike. */
  setMinArcM(v: number): void {
    this.minArcM = v;
  }

  /** Live swap of the pell mesh (e.g. on height change). */
  setPell(pell: THREE.Mesh): void {
    this.pell = pell;
    this.pellHeightM = this.readPellHeightM(pell);
    this.pellRadiusM = this.readPellRadiusM(pell);
  }

  setUserHeightCm(cm: number): void {
    this.userHeightCm = cm;
  }

  /** Sword hand — flips the mandritto/riverso convention in stroke classification. */
  setDominantHand(hand: DominantHand): void {
    this.dominantHand = hand;
  }

  /**
   * Sample the sword and detect at most one strike this frame. The latch guarantees
   * a blade resting inside the pell records exactly one strike, re-arming only once
   * it has fully left. Returns null when there is no fresh impact this frame.
   */
  detect(sword: THREE.Object3D, dt: number): Strike | null {
    this.pell.getWorldPosition(this.pellBase);
    this.pellBase.y = 0;

    const frame = this.tracker.sample(sword, dt);
    if (!frame) return null;

    this.accumulateSwingArc(frame);

    const collider = cylinderCollider(this.pellBase, this.pellHeightM, this.pellRadiusM);
    const cur: BladePose = { base: frame.basePos, tip: frame.tipPos };
    // Sweep the full blade prev→cur so a fast cut is caught at the entry surface,
    // not at whatever deep position the next frame happens to sample.
    const hit = sweepBladeAgainst(this.prevBlade, cur, collider);

    let strike: Strike | null = null;
    if (hit && this.bladeOutsidePell) {
      this.bladeOutsidePell = false;
      strike = this.classifyImpact(frame, hit);
      // The contact consumes the swing; the next colpo accumulates its own arc.
      this.swingArcM = 0;
    }

    // Re-arm the latch once the blade has fully left the pell (evaluated at the
    // current pose). Done after classification so an enter-and-exit within one frame
    // still records exactly one strike.
    if (!bladeInsideCollider(collider, cur.base, cur.tip)) {
      this.bladeOutsidePell = true;
    }
    this.prevBlade = cur;
    return strike;
  }

  /**
   * Grow the swing-arc accumulator by the tip's per-frame travel while the blade is
   * moving; a slow/at-rest tip (between colpi) resets it so each swing measures only
   * its own arc, not the cumulative path since session start.
   */
  private accumulateSwingArc(frame: SwordFrame): void {
    if (frame.tipVelocity.length() < PELL_STRIKE_ARC_MOTION_MPS) {
      this.swingArcM = 0;
      return;
    }
    if (this.prevBlade) this.swingArcM += frame.tipPos.distanceTo(this.prevBlade.tip);
  }

  /** Turn a raw sweep hit into a fully-classified Strike, or null if it fails a gate. */
  private classifyImpact(frame: SwordFrame, hit: SweepHit): Strike | null {
    // Raw single-frame velocity drives speed-based feedback (haptic/audio/thrust gate);
    // the smoothed velocity drives *direction* (cut angle + edge alignment).
    const speed = frame.tipVelocity.length();
    // Validity gate: a real colpo needs both impact speed and a long-enough swing arc.
    if (speed < this.minSpeedMPerSec || this.swingArcM < this.minArcM) return null;
    const thrust = isThrust(frame.tipVelocity, frame.bladeAxis);
    const angle = thrust
      ? 0
      : edgeAlignmentAngle(frame.smoothedTipVelocity, frame.bladeAxis, frame.edgeDir);
    const quality: EdgeQuality | null = thrust ? EDGE_QUALITY.thrust : classifyEdge(angle);
    const localY = hit.local?.['localY'] ?? NaN;
    const zone = zoneFromImpactY(localY, this.pellHeightM);
    if (!quality || !zone) return null;

    // Cut direction = velocity component perpendicular to the blade axis.
    const cutDir = frame.smoothedTipVelocity
      .clone()
      .sub(frame.bladeAxis.clone().multiplyScalar(frame.smoothedTipVelocity.dot(frame.bladeAxis)));
    // Angle in the viewer-facing vertical plane — independent of where on the pell it lands.
    const viewerPos = this.camera.getWorldPosition(new THREE.Vector3());
    const proj = projectToFrontalPlane(cutDir, hit.point, viewerPos);
    const cutAngleDeg =
      !thrust && proj && proj.ratio >= CUT_PROJECTION_MIN_RATIO ? proj.angleDeg : null;
    // Named colpo from the swing arc (cuts only; a thrust is colpo di punta, not a taglio).
    const stroke = thrust ? null : this.classifyStrokeFor(frame);

    return {
      point: hit.point,
      quality,
      zone,
      speed,
      edgeAngleRad: angle ?? 0,
      edgeDir: frame.edgeDir,
      cutDir,
      cutAngleDeg,
      // Read before detect() resets the accumulator for the next colpo (see detect()).
      swingArcM: this.swingArcM,
      stroke,
    };
  }

  /**
   * Classify the colpo from the smoothed impact velocity: world-frame elevation gives
   * inclinazione, lateral component gives lato, component ⊥ to the blade vs the edge
   * gives filo. Returns null if the camera facing is unavailable.
   */
  private classifyStrokeFor(frame: SwordFrame): StrokeClassification | null {
    const camDir = this.camera.getWorldDirection(new THREE.Vector3());
    const forwardHoriz = new THREE.Vector3(camDir.x, 0, camDir.z);
    if (forwardHoriz.lengthSq() < 1e-9) return null;
    forwardHoriz.normalize();
    const userRight = new THREE.Vector3().crossVectors(forwardHoriz, new THREE.Vector3(0, 1, 0));

    return classifyStroke({
      tipVelocityAtImpact: frame.smoothedTipVelocity,
      bladeAxisAtImpact: frame.bladeAxis,
      edgeDirAtImpact: frame.edgeDir,
      userRight,
      dominantHand: this.dominantHand,
    });
  }

  private readPellHeightM(pell: THREE.Mesh): number {
    return (pell.userData['heightM'] as number | undefined) ?? this.userHeightCm / 100;
  }

  private readPellRadiusM(pell: THREE.Mesh): number {
    return (pell.userData['radiusM'] as number | undefined) ?? PELL_DEFAULT_RADIUS_M;
  }
}
